import { useQuery } from "@tanstack/react-query";
import { type Api, listExerciseAttributes } from "api-client";
import { Icon } from "icons";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Popover, PopoverContent, PopoverTrigger } from "ui";

import { WorkoutItemAttributePickerContent } from "./WorkoutItemAttributePickerContent";

export interface WorkoutItemAttributePickerRenderAttributeProps {
  attribute: Api.ListExerciseAttributes.Output.Item;
  label: string;
  selected: boolean;
  disabled: boolean;
  close: () => void;
}

export interface WorkoutItemAttributePickerProps {
  selectedAttributeIds?: string[];
  disabled?: boolean;
  modal?: boolean;
  renderAttribute?: (
    props: WorkoutItemAttributePickerRenderAttributeProps,
  ) => ReactNode;
  onSelect?: (attribute: Api.ListExerciseAttributes.Output.Item) => void;
  onCheckedChange?: (
    exerciseAttributeId: string,
    checked: boolean,
    attribute: Api.ListExerciseAttributes.Output.Item,
  ) => void;
}

export interface WorkoutItemAttributePickerContentProps {
  selectedAttributeIds: string[];
  disabled: boolean;
  attributes: Api.ListExerciseAttributes.Output.Self | undefined;
  isAttributesPending: boolean;
  renderAttribute?: (
    props: WorkoutItemAttributePickerRenderAttributeProps,
  ) => ReactNode;
  onSelect?: (attribute: Api.ListExerciseAttributes.Output.Item) => void;
  onCheckedChange?: (
    exerciseAttributeId: string,
    checked: boolean,
    attribute: Api.ListExerciseAttributes.Output.Item,
  ) => void;
  close: () => void;
}

function useExerciseAttributesQuery() {
  return useQuery({
    queryKey: ["exerciseAttributes", "list"],
    queryFn: async () => {
      const response = await listExerciseAttributes();
      return response.data.data;
    },
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

export function WorkoutItemAttributePicker(
  props: WorkoutItemAttributePickerProps,
) {
  const translation = useTranslation();
  const [open, setOpen] = useState(false);
  const attributesQuery = useExerciseAttributesQuery();

  function close() {
    setOpen(false);
  }

  return (
    <Popover modal={props.modal} open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          aria-label={translation.t("workoutItemSets.attributePickerLabel", {
            defaultValue: "Pick workout attributes",
          })}
          size="icon-sm"
          variant="ghost"
          disabled={props.disabled}
        >
          <Icon name="tabler-list-check" className="size-4" />
        </Button>
      </PopoverTrigger>
      {open ? (
        <PopoverContent align="end" className="w-64 p-0">
          <WorkoutItemAttributePickerContent
            selectedAttributeIds={props.selectedAttributeIds ?? []}
            disabled={props.disabled === true}
            attributes={attributesQuery.data}
            isAttributesPending={attributesQuery.status === "pending"}
            renderAttribute={props.renderAttribute}
            onSelect={props.onSelect}
            onCheckedChange={props.onCheckedChange}
            close={close}
          />
        </PopoverContent>
      ) : null}
    </Popover>
  );
}
